import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TEAM_LABELS } from '@kalkuel-inbox/shared';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  Inbox as InboxIcon,
  Users,
  FileText,
  Megaphone,
  BarChart3,
  Settings as SettingsIcon,
  Plus,
  MessageSquare,
  CornerDownLeft,
} from 'lucide-react';
import { api } from '../lib/api';
import type { ConversationListItem } from '../types';

/** Globales Event, das die InboxPage abfängt, um „Neue Nachricht" zu öffnen. */
export const OPEN_NEW_MESSAGE_EVENT = 'inbox:open-new-message';

interface NavAction {
  id: string;
  label: string;
  hint?: string;
  icon: typeof InboxIcon;
  run: () => void;
}

/**
 * Command-Palette (Cmd/Ctrl+K): Fuzzy-Suche über Konversationen plus
 * Schnellnavigation. Brand-konform, deutsch, ohne Fremd-Lib.
 */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setQDebounced('');
      setActiveIndex(0);
      const id = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
  }, [open]);

  useEffect(() => {
    const id = setTimeout(() => setQDebounced(query.trim()), 200);
    return () => clearTimeout(id);
  }, [query]);

  // useCallback: `navActions` unten hält `go` fest — ohne stabile Referenz und volle
  // Abhängigkeiten liefe dort ein veraltetes `onClose` (react-hooks/exhaustive-deps).
  const go = useCallback(
    (path: string) => {
      navigate(path);
      onClose();
    },
    [navigate, onClose],
  );

  const navActions: NavAction[] = useMemo(
    () => [
      { id: 'nav-inbox', label: 'Posteingang', icon: InboxIcon, run: () => go('/inbox') },
      { id: 'nav-contacts', label: 'Kontakte', icon: Users, run: () => go('/contacts') },
      { id: 'nav-templates', label: 'Vorlagen', icon: FileText, run: () => go('/templates') },
      { id: 'nav-broadcasts', label: 'Broadcasts', icon: Megaphone, run: () => go('/broadcasts') },
      { id: 'nav-reports', label: 'Berichte', icon: BarChart3, run: () => go('/reports') },
      { id: 'nav-settings', label: 'Einstellungen', icon: SettingsIcon, run: () => go('/settings') },
      {
        id: 'action-new-message',
        label: 'Neue Nachricht',
        hint: 'Kontakt anschreiben',
        icon: Plus,
        run: () => {
          navigate('/inbox');
          // Nach dem Navigieren das Modal über ein globales Event öffnen.
          setTimeout(() => window.dispatchEvent(new CustomEvent(OPEN_NEW_MESSAGE_EVENT)), 0);
          onClose();
        },
      },
    ],
    [go, navigate, onClose],
  );

  const filteredNav = useMemo(() => {
    const q = qDebounced.toLowerCase();
    if (!q) return navActions;
    return navActions.filter(
      (a) => a.label.toLowerCase().includes(q) || (a.hint?.toLowerCase().includes(q) ?? false),
    );
  }, [navActions, qDebounced]);

  const { data: convData } = useQuery({
    queryKey: ['command-palette-conversations', qDebounced],
    queryFn: () =>
      api.get<{ items: ConversationListItem[]; nextCursor: string | null }>(
        `/api/conversations?q=${encodeURIComponent(qDebounced)}`,
      ),
    enabled: open && qDebounced.length > 0,
  });
  const conversations = convData?.items ?? [];

  // Flache, navigierbare Trefferliste (Nav-Aktionen zuerst, dann Konversationen).
  const flat = useMemo(
    () => [
      ...filteredNav.map((a) => ({ kind: 'nav' as const, action: a })),
      ...conversations.map((c) => ({ kind: 'conversation' as const, conversation: c })),
    ],
    [filteredNav, conversations],
  );

  useEffect(() => setActiveIndex(0), [qDebounced, flat.length]);

  if (!open) return null;

  function runIndex(idx: number) {
    const item = flat[idx];
    if (!item) return;
    if (item.kind === 'nav') item.action.run();
    else go(`/inbox/${item.conversation.id}`);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      runIndex(activeIndex);
    } else if (e.key === 'Escape') {
      onClose();
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 p-6 pt-24"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-xl overflow-hidden rounded-xl bg-white shadow-brand-lg">
        <div className="relative border-b border-kalkuel-border">
          <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-kalkuel-muted" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Suchen oder navigieren…"
            className="h-12 w-full bg-white pl-11 pr-4 text-sm text-kalkuel-primary placeholder:text-kalkuel-muted focus:outline-none"
          />
        </div>
        <ul className="max-h-[60vh] overflow-y-auto py-1">
          {flat.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-kalkuel-muted">Keine Treffer.</li>
          )}
          {filteredNav.length > 0 && (
            <li className="px-4 pb-1 pt-2 text-[10px] font-medium uppercase tracking-wider text-kalkuel-muted">
              Navigation
            </li>
          )}
          {flat.map((item, idx) => {
            const isActive = idx === activeIndex;
            const base = [
              'flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm',
              isActive ? 'bg-kalkuel-accent-soft' : 'hover:bg-kalkuel-surface',
            ].join(' ');
            if (item.kind === 'nav') {
              const A = item.action.icon;
              return (
                <li key={item.action.id}>
                  <button
                    className={base}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => runIndex(idx)}
                  >
                    <A size={15} className="shrink-0 text-kalkuel-accent2" />
                    <span className="flex-1 text-kalkuel-primary">{item.action.label}</span>
                    {item.action.hint && (
                      <span className="text-[11px] text-kalkuel-muted">{item.action.hint}</span>
                    )}
                    {isActive && <CornerDownLeft size={13} className="text-kalkuel-muted" />}
                  </button>
                </li>
              );
            }
            const c = item.conversation;
            return (
              <li key={c.id}>
                {idx === filteredNav.length && (
                  <div className="px-4 pb-1 pt-2 text-[10px] font-medium uppercase tracking-wider text-kalkuel-muted">
                    Konversationen
                  </div>
                )}
                <button
                  className={base}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => runIndex(idx)}
                >
                  <MessageSquare size={15} className="shrink-0 text-kalkuel-accent2" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-kalkuel-primary">
                      {c.contact.displayName}
                    </span>
                    <span className="block truncate text-[11px] text-kalkuel-muted">
                      {c.messages[0]?.contentText ?? (TEAM_LABELS[c.assignedTeam] ?? c.assignedTeam)}
                    </span>
                  </span>
                  {isActive && <CornerDownLeft size={13} className="text-kalkuel-muted" />}
                </button>
              </li>
            );
          })}
        </ul>
        <footer className="flex items-center gap-4 border-t border-kalkuel-border px-4 py-2 text-[11px] text-kalkuel-muted">
          <span>↑↓ Navigieren</span>
          <span>↵ Öffnen</span>
          <span>Esc Schließen</span>
        </footer>
      </div>
    </div>
  );
}
